<template>
    <div>
        <div class="flex-between">
            <div :class="$style.legend">
                <div>
                    <div :class="$style.item">
                        <div :class="[$style.white, $style.colorBox]" />
                        <span>Current Due</span>
                    </div>
                    <div :class="$style.amount">{{ currentDueAmount }}</div>
                </div>
                <div>
                    <div :class="$style.item">
                        <div :class="[$style.green, $style.colorBox]" />
                        <span>1+ Days Past Due</span>
                    </div>
                    <div :class="$style.amount">
                        {{ pastDueAmountForDays(0, 14) }}
                    </div>
                </div>
                <div>
                    <div :class="$style.item">
                        <div :class="[$style.yellow, $style.colorBox]" />
                        15+ Days Past Due
                    </div>
                    <div :class="$style.amount">
                        {{ pastDueAmountForDays(15, 29) }}
                    </div>
                </div>
                <div>
                    <div :class="$style.item">
                        <div :class="[$style.red, $style.colorBox]" />
                        30+ Days Past Due
                    </div>
                    <div :class="$style.amount">
                        {{ pastDueAmountForDays(30) }}
                    </div>
                </div>
            </div>
        </div>
        <el-table
            id="invoiceTable"
            style="width: 100%"
            max-height="700"
            :data="invoices"
            :row-class-name="tableRowClassName"
            @row-click="rowClicked"
            @selection-change="invoicesSelected"
        >
            <!-- <el-table-column type="selection" /> -->
            <el-table-column width="200" header-align="left" label="Actions">
                <div slot-scope="{ row }" class="d-flex flex-column">
                    <base-button
                        class="mb-2"
                        type="default"
                        size="sm"
                        @click="showFiles(row.id)"
                    >
                        View Files
                    </base-button>
                    <base-button
                        type="success"
                        size="sm"
                        @click="$emit('payInvoices', [row])"
                    >
                        Pay
                    </base-button>
                </div>
            </el-table-column>
            <el-table-column
                v-for="column in tableColumns"
                :key="column.label"
                :width="column.width"
                :min-width="column.minWidth"
                :prop="column.prop"
                :label="column.label"
                :sortable="column.sortable"
                :formatter="column.formatter"
                :sort-method="column.sortMethod"
                :show-overflow-tooltip="true"
            />
        </el-table>
        <file-download-modal
            v-if="showFileDownloadModal"
            :invoice-id="selectedInvoiceID"
            @update:show="showFileDownloadModal = $event"
        />
    </div>
</template>

<script>
import moment from "moment-timezone";
import FileDownloadModal from "src/components/FileDownloadModal";

export default {
    name: "InvoicesTable",
    components: { FileDownloadModal },
    props: {
        invoices: {
            type: Array,
            required: true,
        },
        excludeColumns: {
            type: Array,
            default: () => [],
        },
        currentDueAmount: {
            type: String,
            required: true,
        },
        pastDueAmountForDays: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            selectedInvoiceID: null,
            showFileDownloadModal: false,
            selectedInvoices: [],
            PaymentStatusOutstanding: 1,
            PaymentStatusOutstandingCollections: 2,
            PaymentStatusPaid: 3,
            PaymentStatusWriteOff: 4,
        };
    },
    computed: {
        tableColumns() {
            let columns = [
                {
                    prop: "id",
                    label: "Invoice No.",
                    sortable: true,
                    width: 125,
                },
                {
                    prop: "total",
                    label: "Amount",
                    formatter: (row) => this.formatCurrency(row.total),
                    width: 150,
                    sortable: true,
                },
                {
                    prop: "customer_name",
                    label: "Customer",
                    sortable: true,
                    width: 250,
                },
                {
                    prop: "due_date",
                    label: "Due",
                    sortable: true,
                    sortMethod: (a, b) =>
                        new Date(b.due_date) - new Date(a.due_date),
                    formatter: (row) => this.formatDate(row.due_date),
                    width: 150,
                },
                {
                    prop: "delivery_log_id",
                    label: "Delivery Log",
                    sortable: true,
                    formatter: (row) =>
                        row.delivery_log_id === 0 ? "--" : row.delivery_log_id,
                    width: 150,
                },
                {
                    prop: "date",
                    label: "Delivery Date",
                    sortable: true,
                    sortMethod: (a, b) => new Date(b.date) - new Date(a.date),
                    formatter: (row) => this.formatDate(row.date),
                    width: 150,
                },
                {
                    prop: "past_due",
                    label: "Days Past Due",
                    sortable: true,
                    formatter: (row) => this.getPastDueDays(row.due_date),
                    width: 150,
                },
                {
                    prop: "interest_due",
                    label: "Interest Due",
                    formatter: (row) => this.formatCurrency(row.interest_due),
                    sortable: true,
                    width: 150,
                },
                {
                    prop: "interest_applied",
                    label: "Interest Applied",
                    formatter: (row) =>
                        row.apply_interest
                            ? this.formatCurrency(row.interest_due)
                            : "--",
                    sortable: true,
                    width: 150,
                },
                {
                    prop: "payment_status",
                    label: "Status",
                    formatter: (row) => this.formatPaymentStatus(row),
                    width: 150,
                },
                {
                    prop: "notes",
                    label: "Notes",
                    width: 200,
                },
            ];
            // eslint-disable-next-line max-len
            columns = columns.filter(
                (column) =>
                    this.excludeColumns.findIndex(
                        (hide) => hide === column.label,
                    ) === -1,
            );
            return columns;
        },
    },
    methods: {
        showFiles(invoiceID) {
            this.selectedInvoiceID = invoiceID;
            this.showFileDownloadModal = true;
        },
        invoicesSelected(invoices) {
            this.selectedInvoices = invoices;
        },
        formatDate(date) {
            if (date) {
                return moment(date).format("MM/DD/YYYY");
            }
            return "--";
        },
        formatPaymentStatus(row) {
            switch (row.payment_status) {
                case this.PaymentStatusOutstanding:
                    return "Outstanding";
                case this.PaymentStatusOutstandingCollections:
                    return "Collections";
                case this.PaymentStatusPaid:
                    return "Paid";
                case this.PaymentStatusWriteOff:
                    return "Write Off";
                default:
                    return "unknown";
            }
        },
        getPastDueDays(dueDate) {
            if (dueDate) {
                const now = moment();
                const daysPast = Math.floor(now.diff(dueDate, "days", true));
                return daysPast >= 0 ? daysPast : 0;
            }
            return 0;
        },
        formatCurrency(value) {
            const formatter = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
            });
            return formatter.format(value);
        },
        tableRowClassName({ row }) {
            const today = moment();
            const due = moment(row.due_date);
            const diff = today.diff(due, "days");
            if (diff >= 30) {
                return this.$style.red;
            }
            if (diff >= 15) {
                return this.$style.yellow;
            }
            if (diff > 0) {
                return this.$style.green;
            }
            return "";
        },
        rowClicked(row, col, event) {
            if (!event.target.classList.contains("fa")) {
                this.$emit("payInvoice", row);
            }
        },
    },
};
</script>

<style module>
.legend {
    display: flex;
}

.item {
    display: flex;
    padding-right: 20px;
}

.amount {
    padding-left: 30px;
}

.colorBox {
    width: 20px;
    height: 20px;
    display: inline-flex;
    margin-right: 10px;
    border: 1px solid gray;
}

.red {
    background-color: rgba(255, 0, 0, 0.3) !important;
}

.yellow {
    background-color: rgba(255, 255, 0, 0.3) !important;
}

.green {
    background-color: rgba(0, 255, 0, 0.3) !important;
}

.white {
    background-color: white !important;
}
</style>
